"use client";

import "@rainbow-me/rainbowkit/styles.css";

import { useState } from "react";
import dynamic from "next/dynamic";
import { RainbowKitProvider, darkTheme } from "@rainbow-me/rainbowkit";
import { WagmiProvider } from "wagmi";

import { SolanaSessionScope } from "@/hooks/use-solana-wallet-session";
import { GlyphAccountProvider } from "@/lib/wallet/GlyphAccountProvider";
import { PhantomWalletBridge } from "@/lib/wallet/PhantomWalletBridge";
import { isGenericEnabled } from "@/lib/wallet/solana/allowlist";
import { createWagmiConfig } from "@/lib/wallet/wagmi-config";

import type { ReactNode } from "react";

// Load the external Wallet Standard picker only when configured.
const SolanaWalletBridge = dynamic(() =>
  import("@/lib/wallet/solana/SolanaWalletBridge").then(
    (module) => module.SolanaWalletBridge
  )
);

/** Only external wallets may deliver assets or sign Solana transactions. */
export function solanaBridge(): "phantom" | "generic" {
  return isGenericEnabled() ? "generic" : "phantom";
}

// Matches the brand gold used by the site's primary controls.
const theme = darkTheme({
  accentColor: "#c7ad72",
  accentColorForeground: "#171208",
  borderRadius: "large",
  overlayBlur: "small",
});

/** Requires a query client above it, which the root layout already provides. */
export function LiveWalletProvider({
  children,
  returning,
}: {
  children: ReactNode;
  returning?: boolean;
}) {
  const [config] = useState(createWagmiConfig);
  return (
    <WagmiProvider config={config}>
      <RainbowKitProvider theme={theme} modalSize="compact">
        {/* Glyph supplies identity inside the external wallet bridge. It
            never supplies the asset address or Solana signer. */}
        {solanaBridge() === "generic" ? (
          <SolanaWalletBridge returning={returning}>
            <GlyphAccountProvider>
              <SolanaSessionScope>{children}</SolanaSessionScope>
            </GlyphAccountProvider>
          </SolanaWalletBridge>
        ) : (
          <PhantomWalletBridge returning={returning}>
            <GlyphAccountProvider>
              <SolanaSessionScope>{children}</SolanaSessionScope>
            </GlyphAccountProvider>
          </PhantomWalletBridge>
        )}
      </RainbowKitProvider>
    </WagmiProvider>
  );
}
